import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus } from "lucide-react";
import { toast } from "sonner";

const CLASS_LABELS = {
  1: "Capitaux propres & dettes",
  2: "Immobilisations",
  3: "Stocks",
  4: "Tiers (Clients/Fournisseurs)",
  5: "Trésorerie (Banques/Caisses)",
  6: "Charges",
  7: "Produits",
};

export default function PlanComptablePage() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", class_num: 6, type: "charge" });
  const [filter, setFilter] = useState("ALL");

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/accounts");
      setAccounts(data);
    } catch {
      toast.error("Erreur de chargement du plan comptable");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code || !form.name) return toast.error("Code et libellé requis");
    try {
      await api.post("/accounting/accounts", { ...form, class_num: Number(form.class_num) });
      toast.success("Compte créé");
      setOpen(false);
      setForm({ code: "", name: "", class_num: 6, type: "charge" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const filtered = filter === "ALL" ? accounts : accounts.filter(a => String(a.class_num) === filter);
  const byClass = {};
  filtered.forEach(a => { (byClass[a.class_num] ||= []).push(a); });

  return (
    <div className="space-y-6" data-testid="plan-comptable-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BookOpen className="h-3 w-3" /><span>PLAN COMPTABLE — SYSCOHADA</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Plan comptable</h1>
          <p className="text-slate-500 mt-1">{accounts.length} comptes paramétrés — classes 1 à 7.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toutes les classes</SelectItem>
              {Object.entries(CLASS_LABELS).map(([n, l]) => <SelectItem key={n} value={n}>Classe {n} — {l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="open-new-account"><Plus className="h-4 w-4 mr-2" />Nouveau compte</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Créer un compte</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Code (6 chiffres)</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.replace(/\D/g, "").slice(0,6)})} placeholder="612000" data-testid="account-code" /></div>
                <div><Label>Libellé</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Locations diverses" data-testid="account-name" /></div>
                <div>
                  <Label>Classe</Label>
                  <Select value={String(form.class_num)} onValueChange={(v) => setForm({...form, class_num: Number(v)})}>
                    <SelectTrigger data-testid="account-class"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CLASS_LABELS).map(([n, l]) => <SelectItem key={n} value={n}>Classe {n} — {l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                    <SelectTrigger data-testid="account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actif">Actif</SelectItem>
                      <SelectItem value="passif">Passif</SelectItem>
                      <SelectItem value="charge">Charge</SelectItem>
                      <SelectItem value="produit">Produit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button className="bg-[#1E3A8A] text-white" onClick={save} data-testid="save-account">Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {Object.entries(byClass).map(([cls, accs]) => (
        <div key={cls} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <h2 className="font-heading font-bold text-[#1E3A8A]">Classe {cls} — {CLASS_LABELS[cls]} <span className="text-sm font-normal text-slate-500">({accs.length})</span></h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Libellé</th>
                <th className="px-4 py-2">Type</th>
              </tr>
            </thead>
            <tbody>
              {accs.map(a => (
                <tr key={a.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`account-${a.code}`}>
                  <td className="px-4 py-2 font-mono text-xs text-[#1E3A8A] font-bold">{a.code}</td>
                  <td className="px-4 py-2">{a.name}</td>
                  <td className="px-4 py-2"><span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-xs uppercase font-semibold">{a.type}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {accounts.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500">Plan comptable vide.</div>
          <div className="text-xs text-slate-400 mt-1">Allez sur le Tableau de bord comptable et cliquez &quot;Initialiser plan comptable&quot;.</div>
        </div>
      )}
    </div>
  );
}
